import { useEffect, useMemo, useState } from 'react';
import FilterPanel from '../../FilterLayout/FilterPanel';

import {
  IData,
  IDepartment,
  IOrganisation,
  IRegion,
  OptionType,
  OptionTypes,
} from '../../../../../shared/types/filters';
import { useWidgetsModalContext } from '../../../../../shared/providers/WidgetsModalProvider';
import useFilters from '../../../../../utilities/hooks/useFilters';
import { useDeviceType } from '../../../../../utilities/common/deviceType';
import { FilterKeys } from '../../../../../shared/constants/filter/filter';
import { useFilterData } from '../../../../../utilities/hooks/useFilterData';
import { convertToNestedFormat } from '../../../../../utilities/helper/departments';

const createRegionOption = ({
  countryCode,
  countryName,
}: IRegion): OptionType<string> => {
  return {
    label: countryName,
    value: countryCode,
  };
};

const createOrgtOption = ({ name, id }: IOrganisation): OptionTypes<number> => {
  return {
    label: name,
    value: id,
  };
};

const flattenOrganisationArray = (orgArray?: IOrganisation[]) => {
  const result = [] as IOrganisation[];
  if (orgArray) {
    orgArray.forEach(function (org) {
      result.push(org);
    });
  }
  return result;
};

interface IOrganisationFilterProps {
  data: any;
  mobileSheet?: boolean;
  allOrganisations?: (args: any) => void;
  callBackDept?: any;
  callBackRegion?: any;
}

export default function OrganisationFilter({
  data,
  mobileSheet,
  callBackDept,
  callBackRegion,
}: IOrganisationFilterProps) {
  const [queryDataDept, setQueryDataDept] = useState<IData>({ teams: [] });
  const [queryDataRegion, setQueryDataRegion] = useState<IData>({
    countries: [],
  });

  const {
    selectedOrganisations,
    toggleOrganisations,
    selectOrDeselectAllOrganisations,
    selectedAllOrg,
    toggleAllOrg,
    selectedDepartments,
    selectOrDeselectAllDepartments,
    regionOptions,
    regionFilter,
    setRegionFilter,
    setRegionOptions,
  } = useFilters();
  const { configData } = useFilterData();

  const { selectedAccorionFilter } = useWidgetsModalContext();
  const isMobile = useDeviceType();

  const allFalse = Array.from(selectedOrganisations.values()).every(
    (value) => value === false,
  );

  const organisationOptions = useMemo(
    () => data?.map((option: IOrganisation) => createOrgtOption(option)) ?? [],
    [data],
  );

  const flatOrganisationArray = useMemo(
    () => flattenOrganisationArray(data),
    [data],
  );

  function getSelectedOrgs() {
    const selected = [] as OptionTypes<number>[];
    Array.from(selectedOrganisations)
      .filter(([_orgId, exists]) => exists)
      .forEach(([orgId]) => {
        const res = flatOrganisationArray.find((org) => org.id === orgId);
        if (res) {
          selected.push({ label: res.name, value: res.id });
        }
      });
    return selected;
  }

  const isOrganisationSelected = (id: number) =>
    selectedOrganisations.get(id) ?? false;

  function onOrganisationChange(id: number) {
    const selectedOrg = flatOrganisationArray.find((org) => org.id === id);
    if (selectedOrg) {
      toggleOrganisations(selectedOrg);
    }
  }

  useEffect(() => {
    if (allFalse) {
      selectOrDeselectAllOrganisations(new Map());
    }
  }, [allFalse]);

  useEffect(() => {
    if (selectedOrganisations.size > 0 && selectedAllOrg) {
      const allTeams = selectedAllOrg.filter((org: IOrganisation) =>
        Array.from(selectedOrganisations).some(
          ([id, value]) => value && id === org.id,
        ),
      );

      const uniqueRegionMap = new Map<number, IRegion>();
      const uniqueDeptMap = new Map<number, IDepartment>();

      allTeams.forEach((org: IOrganisation) => {
        org.operationalCountries?.forEach((reg: IRegion) =>
          uniqueRegionMap.set(reg.id, reg),
        );

        if (Array.isArray(org.teams)) {
          org.teams.forEach((team: IDepartment) =>
            uniqueDeptMap.set(team.id, team),
          );
        }
      });

      setQueryDataRegion({ countries: Array.from(uniqueRegionMap.values()) });
      setQueryDataDept({
        teams: convertToNestedFormat(Array.from(uniqueDeptMap.values())),
      });
    }
  }, [selectedOrganisations, selectedAllOrg]);

  useEffect(() => {
    if (queryDataDept?.teams && queryDataDept.teams.length > 0) {
      const newSelectedDepartments = new Map<number, boolean>();
      selectedDepartments.forEach((value, key) => {
        if (
          queryDataDept.teams &&
          selectedAllOrg?.some(
            (org) =>
              selectedOrganisations.get(org.id) &&
              org.teams?.some((dept) => dept.id === key),
          )
        ) {
          newSelectedDepartments.set(key, value);
        }
      });
      selectOrDeselectAllDepartments(newSelectedDepartments);
    }
    callBackDept(queryDataDept);
  }, [queryDataDept]);

  useEffect(() => {
    if (allFalse && selectedOrganisations.size !== 0)
      selectOrDeselectAllDepartments(new Map());
  }, [selectedOrganisations]);

  useEffect(() => {
    configData &&
      Array.from(selectedOrganisations.values()).every(
        (value) => value === false,
      ) &&
      setQueryDataRegion(configData);
  }, [configData, selectedAccorionFilter]);

  useEffect(() => {
    const options =
      queryDataRegion?.countries?.map((option) => createRegionOption(option)) ??
      [];
    setRegionOptions(options);
    callBackRegion(queryDataRegion);
  }, [queryDataRegion]);

  useEffect(() => {
    const availableCountries = regionOptions
      .filter((option) => {
        return (
          queryDataRegion.countries &&
          queryDataRegion.countries.some(
            (country) => country.countryCode === option.value,
          )
        );
      })
      .map((option) => option.value);
    const updatedRegionFilter = regionFilter.filter((country) =>
      availableCountries.includes(country),
    );
    setTimeout(() => {
      setRegionFilter(updatedRegionFilter);
    }, 100);
  }, [regionOptions]);

  const selectAll = (
    isChecked: boolean,
    optionsToSelect: any,
    isMatching?: boolean,
  ) => {
    if (isChecked) {
      if (isMatching) {
        selectOrDeselectAllOrganisations(
          new Map(
            [...selectedOrganisations].filter(
              ([id]) => !optionsToSelect.includes(id),
            ),
          ),
        );
      } else {
        selectOrDeselectAllOrganisations(new Map());
        selectOrDeselectAllDepartments(new Map());
        setRegionFilter([]);
      }
    } else if (optionsToSelect.length > 0) {
      const selectedOrgs = isMatching
        ? new Map(selectedOrganisations)
        : new Map();
      optionsToSelect.forEach((id: any) => {
        selectedOrgs.set(id, true);
      });
      selectOrDeselectAllOrganisations(selectedOrgs);
    } else {
      const newSelectedOrgs = new Map();
      flatOrganisationArray.forEach(({ id }) => {
        newSelectedOrgs.set(id, true);
      });
      selectOrDeselectAllOrganisations(newSelectedOrgs);
    }
  };

  useEffect(() => {
    toggleAllOrg(data);
  }, [data]);

  useEffect(() => {
    if (allFalse) selectOrDeselectAllOrganisations(new Map());
  }, []);

  // useAllSelected(
  //   FilterKeys.organisation,
  //   selectedOrganisations,
  //   flatOrganisationArray,
  // );

  if (!data) {
    return null;
  }

  return (
    <FilterPanel<number>
      name="organisations"
      filterKey={FilterKeys.organisation}
      mobileSheet={mobileSheet}
      options={organisationOptions}
      isSelected={isOrganisationSelected}
      onSelectedChange={onOrganisationChange}
      selectedItems={getSelectedOrgs()}
      selectAllItems={selectAll}
      flatItemArray={flatOrganisationArray}
    />
  );
}
